<template>
  <div id="keywordSelectionContainer">
    <div class="d-flex justify-center">
      <div class="article-content">
        <div class="d-flex flex-column text-center mt-1">
      <div class="text-h5">
        {{ $t("keywords.keywordSelection") }}
        <strong>{{ displayMainKeyword }}</strong>
        <v-btn
          v-if="showAnalysisToggle"
          class="ml-4 pl-3"
          color="primary"
          @click="toggleKeywordAnalysis"
        >
          <v-icon v-if="keywordSelectionShown">
            expand_less
          </v-icon>
          <v-icon v-else>
            expand_more
          </v-icon>
          {{ $t("keywords.allKeywords") }}
        </v-btn>
      </div>
      <div class="my-2">
        <template v-if="!isAnalyze">
          {{ $t("keywords.keywordSelectionCaption") }}
        </template>
        <template v-else>
          {{ $t('keywords.analyze.keywordSelectionCaption.begin') }}
          <strong>{{ $t('keywords.analyze.keywordSelectionCaption.bold') }}</strong>{{ $t('keywords.analyze.keywordSelectionCaption.end') }}
        </template>
      </div>
      <div
        class="d-flex flex-wrap justify-center mt-2"
      >
        <v-chip
          small
          class="ma-1 text-none"
          color="primary"
          outlined
        >
          {{ $t('createBrief.templates.' + (articleMetadata.geoTemplate || 'universal') + '.title') }}
        </v-chip>
        <v-chip
          small
          class="ma-1 text-none"
          outlined
        >
          {{ $t('createBrief.tones.' + (articleMetadata.tone || 'professional')) }}
        </v-chip>
        <v-chip
          small
          class="ma-1 text-none"
          outlined
        >
          {{ $t('createBrief.audiences.' + (articleMetadata.audience || 'general')) }}
        </v-chip>
        <v-chip
          small
          class="ma-1 text-none"
          outlined
        >
          {{ $t('createBrief.depths.' + (articleMetadata.depth || 'standard')) }}
        </v-chip>
        <v-chip
          v-for="word in (articleMetadata.secondaryKeywords || [])"
          :key="'sec-' + word"
          small
          class="ma-1 text-none"
          outlined
          color="primary"
        >
          {{ word }}
        </v-chip>
      </div>
      <p
        v-if="enhanceWaitingForPage"
        class="caption text-center mt-3 mb-0"
        data-test="enhance-loading-page"
      >
        {{ $t('article.manualEnhance.loadingPage') }}
      </p>
      <v-card
        v-if="showLucyFork"
        outlined
        class="pa-4 mt-4 kw-lucy-fork"
      >
        <h3 class="text-subtitle-1 font-weight-bold mb-1">
          {{ lucyForkTitle }}
        </h3>
        <p class="caption text--secondary mb-4">
          {{ lucyForkLead }}
        </p>
        <v-row dense>
          <v-col
            cols="12"
            md="6"
          >
            <WorkspaceControlTip
              :text="lucyForkImmediateTip"
              block
            >
              <v-btn
                color="accent"
                depressed
                block
                large
                class="text-none"
                :data-test="lucyForkImmediateReady ? 'lucy-fork-generate' : 'lucy-fork-waiting'"
                :title="lucyForkImmediateReady ? undefined : lucyForkImmediateTip"
                :disabled="!lucyForkImmediateReady"
                :loading="lucyForkImmediateLoading"
                @click="onGenerovatHned"
              >
                <v-icon
                  left
                  small
                >
                  flash_on
                </v-icon>
                {{ lucyForkImmediateLabel }}
              </v-btn>
            </WorkspaceControlTip>
            <p class="caption text-center mt-2 mb-0">
              {{ lucyForkImmediateLine }}
            </p>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <WorkspaceControlTip
              :text="lucyForkCustomizeTip"
              block
            >
              <v-btn
                outlined
                block
                large
                class="text-none"
                color="primary"
                data-test="lucy-fork-customize"
                @click="onUpravitPredGenerovanim"
              >
                <v-icon
                  left
                  small
                >
                  tune
                </v-icon>
                {{ lucyForkCustomizeLabel }}
              </v-btn>
            </WorkspaceControlTip>
            <p class="caption text-center mt-2 mb-0">
              {{ lucyForkCustomizeLine }}
            </p>
          </v-col>
        </v-row>
        <p
          v-if="lucyGenerateBlockMessage"
          class="caption warning--text text-center mt-4 mb-0"
        >
          {{ lucyGenerateBlockMessage }}
        </p>
      </v-card>
      <v-card
        v-if="showLucyFastSummary"
        outlined
        class="pa-3 mt-3 kw-fast-summary"
      >
        <div class="d-flex align-start">
          <span class="kw-lucy-avatar">L</span>
          <div>
            <strong>{{ $t('keywords.lucy.fastSummaryTitle', [lucySlotCount]) }}</strong>
            <p class="caption text--secondary mb-0 mt-1">
              {{ $t('keywords.lucy.fastSummaryDetail') }}
            </p>
          </div>
        </div>
      </v-card>
      <div
        v-if="!disableSelection && !noInspirations"
        class="kw-paragraph-counter mt-4 mb-2 px-2"
      >
        <div class="d-flex flex-wrap align-center justify-center">
          <strong>{{ $t('keywords.lucy.paragraphsCounter', [lucySlotCount, maxAiKeywords]) }}</strong>
          <WorkspaceControlTip
            class="ml-1 d-inline-flex"
            :text="$t('keywords.lucy.tip.limit20')"
            icon-only
          />
          <v-chip
            small
            class="ml-2 font-weight-bold"
            :color="lucySlotCount > maxAiKeywords ? 'warning' : lucySlotCount === 0 ? 'grey' : 'accent'"
          >
            {{ lucySlotCount }} / {{ maxAiKeywords }}
          </v-chip>
        </div>
        <p
          class="caption text-center mb-0 mt-1"
          :class="lucyLimitMessageClass"
        >
          {{ lucyLimitMessage }}
        </p>
      </div>
      <AiWritingBriefPanel
        ref="aiWritingBriefPanel"
        class="mt-4"
        :brief="aiWritingBrief"
        :loading="loadingBrief"
        :error="aiBriefError"
        :current-template="articleMetadata.geoTemplate"
        :read-only="disableSelection"
        :is-analyze="isAnalyze"
        :defer-write-cta="showLucyFork"
        :write-loading="loadingSave"
        @apply-template="applySuggestedTemplate"
        @generate="saveSelectedKeyword(undefined, true)"
        @keep-shape="keepAnswerShape"
        @work-in-live="workInLiveAnswer"
        @toggle-citation="toggleCitation"
      />
      <div
        v-if="showPrimaryWriteAction"
        class="d-flex justify-center mt-4 keyword-write-cta-slot"
      >
        <v-tooltip
          top
          :disabled="!writeCtaTooltip"
        >
          <template #activator="{ on }">
            <span v-on="on">
              <v-btn
                data-test="enhance-article"
                :disabled="writeCtaDisabled"
                :loading="loadingSave"
                class="text-none"
                color="accent"
                depressed
                @click="onPrimaryWriteClick"
              >
          <v-icon class="mr-2">
            send
          </v-icon>
          {{ primaryWriteLabel }}
              </v-btn>
            </span>
          </template>
          {{ writeCtaTooltip }}
        </v-tooltip>
      </div>
    </div>
    <v-slide-y-transition>
      <div v-show="keywordSelectionShown">
        <p
          v-if="readOnly && !disableSelection"
          class="text-center text--secondary mt-4 mb-2 px-4"
        >
          {{ $t('keywords.reviewKeywordsHint') }}
        </p>
        <v-slide-y-transition hide-on-leave>
          <div>
            <div
              id="keywordTables"
              ref="keywordTablesAnchor"
            />
            <div
              class="keyword-workspace-groups"
            >
              <div class="kw-pair">
                <KeywordWorkspaceGroup
                  group-id="g-related"
                  :title="$t('keywords.list.workspace.related')"
                  :subtitle="$t('keywords.list.workspace.relatedSub')"
                  :count-label="groupCountLabel(relatedKeywords.length)"
                  blue
                  :open="analysisOpen.related"
                  @toggle="toggleAnalysisGroup('related')"
                >
                  <KeywordsList
                    v-if="!loading"
                    :keyword-type="KeywordType.RELATED"
                    :keywords="relatedKeywords"
                    :read-only="disableSelection"
                    :article="articleMetadata"
                    :current-selected-keyword.sync="selectedKeyword"
                    :selected-for-ai.sync="keywordsForAi"
                    @change-main-keyword="changeMainKeyword"
                    @insert-lsi="insertBriefLsi"
                  />
                  <v-skeleton-loader
                    v-else
                    type="table-row@4"
                    class="pa-4"
                  />
                </KeywordWorkspaceGroup>
                <KeywordWorkspaceGroup
                  group-id="g-questions"
                  :title="$t('keywords.list.workspace.questions')"
                  :subtitle="$t('keywords.list.workspace.questionsSub')"
                  :count-label="groupCountLabel(questionKeywords.length)"
                  blue
                  :open="analysisOpen.questions"
                  @toggle="toggleAnalysisGroup('questions')"
                >
                  <KeywordsList
                    v-if="!loading"
                    :keyword-type="KeywordType.QUESTION"
                    :keywords="questionKeywords"
                    :read-only="disableSelection"
                    :article="articleMetadata"
                    :current-selected-keyword.sync="selectedKeyword"
                    :selected-for-ai.sync="keywordsForAi"
                    @change-main-keyword="changeMainKeyword"
                    @insert-lsi="insertBriefLsi"
                  />
                  <v-skeleton-loader
                    v-else
                    type="table-row@4"
                    class="pa-4"
                  />
                </KeywordWorkspaceGroup>
              </div>
              <KeywordWorkspaceGroup
                group-id="g-lsi"
                :title="$t('keywords.list.workspace.lsi')"
                :subtitle="$t('keywords.list.workspace.lsiSub')"
                :count-label="String(variationKeywords.length)"
                blue
                :open="analysisOpen.lsi"
                @toggle="toggleAnalysisGroup('lsi')"
              >
                <KeywordsList
                  v-if="!loading"
                  :keyword-type="KeywordType.VARIATION"
                  :keywords="variationKeywords"
                  :read-only="disableSelection"
                  :article="articleMetadata"
                  :current-selected-keyword.sync="selectedKeyword"
                  :selected-for-ai.sync="keywordsForAi"
                  @change-main-keyword="changeMainKeyword"
                  @insert-lsi="insertBriefLsi"
                />
                <v-skeleton-loader
                  v-else
                  type="table-row@4"
                  class="pa-4"
                />
              </KeywordWorkspaceGroup>
              <KeywordWorkspaceGroup
                group-id="g-long"
                :title="$t('keywords.list.workspace.longTail')"
                :subtitle="$t('keywords.list.workspace.longTailSub')"
                :count-label="String(longTailKeywords.length)"
                blue
                :open="analysisOpen.long"
                @toggle="toggleAnalysisGroup('long')"
              >
                <KeywordsList
                  v-if="!loading"
                  :keyword-type="KeywordType.LONG_TAIL"
                  :keywords="longTailKeywords"
                  :read-only="disableSelection"
                  :article="articleMetadata"
                  :current-selected-keyword.sync="selectedKeyword"
                  :selected-for-ai.sync="keywordsForAi"
                  @change-main-keyword="changeMainKeyword"
                  @insert-lsi="insertBriefLsi"
                />
                <v-skeleton-loader
                  v-else
                  type="table-row@4"
                  class="pa-4"
                />
              </KeywordWorkspaceGroup>
              <KeywordWorkspaceGroup
                v-if="autocompletePhraseItems.length || (loadingBrief && !briefHasContent)"
                group-id="g-ac"
                :title="$t('keywords.list.workspace.autocomplete')"
                :subtitle="$t('keywords.list.workspace.autocompleteSub')"
                :count-label="String(autocompletePhraseItems.length)"
                blue
                :open="analysisOpen.ac"
                @toggle="toggleAnalysisGroup('ac')"
              >
                <KeywordTopicChips
                  :title="$t('keywords.list.workspace.colPhrase')"
                  :items="autocompletePhraseItems"
                  :keyword-type="KeywordType.RELATED"
                  source-chip="ac"
                  :loading="loadingBrief && !autocompletePhraseItems.length"
                  :read-only="disableSelection"
                  :selected-for-ai.sync="keywordsForAi"
                />
              </KeywordWorkspaceGroup>
              <KeywordWorkspaceGroup
                v-if="gapTopicItems.length || showGapSkeleton"
                group-id="g-gap"
                :title="$t('keywords.list.workspace.gap')"
                :subtitle="$t('keywords.list.workspace.gapSub')"
                :count-label="String(gapTopicItems.length)"
                blue
                :open="analysisOpen.gap"
                @toggle="toggleAnalysisGroup('gap')"
              >
                <KeywordTopicChips
                  :title="$t('keywords.list.workspace.colTopic')"
                  :items="gapTopicItems"
                  :keyword-type="KeywordType.RELATED"
                  source-chip="top"
                  show-volume
                  :loading="showGapSkeleton"
                  :read-only="disableSelection"
                  :selected-for-ai.sync="keywordsForAi"
                />
              </KeywordWorkspaceGroup>
            </div>
            <div
              v-if="!disableSelection"
              class="d-flex flex-column align-center mt-2 mb-2"
            >
              <v-btn
                text
                class="text-none"
                @click="showPromptEditor = !showPromptEditor"
              >
                <v-icon
                  left
                  small
                >
                  {{ showPromptEditor ? 'expand_less' : 'edit' }}
                </v-icon>
                {{ showPromptEditor ? $t('keywords.hideBrief') : $t('keywords.editBrief') }}
              </v-btn>
              <AIPrompt
                v-if="showPromptEditor || promptRequired"
                v-model="aiPrompt"
                :no-prompt="articleMetadata.noAiPrompt"
                :read-only="disableSelection"
                class="align-self-stretch"
              />
            </div>
          </div>
        </v-slide-y-transition>
        <div
          v-if="!$store.getters.sharePreview"
          class="d-flex justify-center"
        >
          <div class="d-flex flex-column align-center">
            <span class="mb-2 d-flex flex-row flex-nowrap">
              <span>
                {{ $t('keywords.info.selectedForAi') }}
              </span>
              <v-card
                class="ml-2 px-2"
                :color="aiMeterColor"
                disabled
                depressed
              >
                <span>
                  {{ lucySlotCount }}
                </span>
                /
                <span>
                  {{ maxAiKeywords }}
                </span>
              </v-card>
            </span>
            <span
              v-if="showCancelArticle"
              class="mb-2"
            >
              {{ $t('keywords.info.beforeButtonNoData') }}
            </span>
            <span
              v-else-if="!readOnly"
              class="mb-2"
            >
              {{ $t('keywords.info.beforeButton') }}
            </span>
            <v-slide-y-reverse-transition hide-on-leave>
              <div
                v-show="selectNewKeyword"
                class="keyword-new-select mt-3"
              >
                <KeywordInput
                  is-edit
                  :loading="loading"
                  :query-params="{}"
                  :article-in-progress="articleMetadata"
                  @input="onKeywordInput"
                />
              </div>
            </v-slide-y-reverse-transition>
            <v-row>
              <v-col
                v-if="showCancelArticle"
                class="d-flex justify-center"
              >
                <v-tooltip
                  bottom
                  :disabled="articleCreatedInThisPeriod"
                >
                  <template #activator="{ on }">
                    <span v-on="on">
                      <v-btn
                        :loading="loadingSave"
                        class="bg-blue-darken-4 text-white mt-4 mb-n2"
                        color="warning"
                        depressed
                        :disabled="!articleCreatedInThisPeriod"
                        @click="deleteArticleAndRefundCredit"
                      >
                        <v-icon class="mr-2">
                          cancel
                        </v-icon>
                        {{ $t(cancelArticleLabelKey) }}
                      </v-btn>
                    </span>
                  </template>
                  <span>
                    {{ $t('keywords.notCreatedInThisBillingPeriod') }}
                  </span>
                </v-tooltip>
              </v-col>
              <v-col
                class="d-flex flex-wrap justify-center align-center text-center"
                :cols="!showCancelArticle ? 12 : undefined"
              >
                <v-tooltip
                  v-if="!articleWriteFinished"
                  bottom
                  :disabled="canContinue"
                >
                  <template #activator="{ on }">
                    <span v-on="on">
                      <v-btn
                        id="generateInspirationButton"
                        :disabled="writeCtaDisabled"
                        :loading="loadingSave"
                        class="bg-blue-darken-4 text-white mt-4 mb-6"
                        :class="{
                          'multi-line-button': !noInspirations
                        }"
                        color="accent"
                        depressed
                        @click="saveSelectedKeyword(undefined, true)"
                      >
                        <template v-if="noInspirations">
                          <v-icon class="mr-2">
                            send
                          </v-icon>
                          {{ $t('keywords.generateInspirationAnyway') }}
                        </template>
                        <template v-else>
                          <v-icon class="mr-2">
                            send
                          </v-icon>
                          {{ primaryWriteLabel }}
                        </template>
                      </v-btn>
                    </span>
                  </template>
                  <span>
                    {{ $t('keywords.info.tooManyKeywordsForAi') }}
                  </span>
                </v-tooltip>
                <v-btn
                  class="ml-2 mt-4 mb-6"
                  text
                  @click="toggleNewKeywordSelect"
                >
                  <v-icon v-if="selectNewKeyword">
                    expand_more
                  </v-icon>
                  <v-icon v-else>
                    expand_less
                  </v-icon>
                  {{ $t("keywords.selectNewKeyword") }}
                </v-btn>
                <v-btn
                  v-if="readOnly"
                  class="ml-2 mt-4 mb-6"
                  text
                  @click="closeKeywordAnalysis"
                >
                  <v-icon>
                    close
                  </v-icon>
                  {{ $t("keywords.closeAnalysis") }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </v-slide-y-transition>
      </div>
    </div>
    <v-row id="keywordSelectionAfter" />
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import KeywordsList from '@/components/keywords/KeywordsList.vue';
    import KeywordTopicChips from '@/components/keywords/KeywordTopicChips.vue';
    import KeywordWorkspaceGroup from '@/components/keywords/KeywordWorkspaceGroup.vue';
    import {KeywordType} from '@/enum/keyword_type';
    import {Functions} from '@/plugins/Functions';
    import {InspirationResponse} from '@/model/DataForSeo';
    import {Article, ArticleMetadata, Keyword, KeywordForAi, ArticleQuestion} from "@/model/Article";
    import AIPrompt from "@/components/ai/AIPrompt.vue";
    import KeywordInput from "@/components/keywords/KeywordInput.vue";
    import AiWritingBriefPanel from "@/components/keywords/AiWritingBriefPanel.vue";
    import WorkspaceControlTip from '@/components/keywords/WorkspaceControlTip.vue';
    import {
        buildDefaultKeywordsForAi,
        countLucySlots,
        pickDefaultCitationUrls,
    } from '@/service/keywordLucySelection';
    import {Firestore} from "@/plugins/Firestore";
    import {ArticleCreditUsed, cancelArticleButtonKey} from '@/service/articleCancelRefund';
    import {scrollIntoViewIfNotVisible, focusWorkspaceAnchor} from "@/utils/scroll";
    import {collapseAnalysisAndScrollToHBlocks} from "@/service/articleWriteWorkspace";
    import {EventBus} from "@/utils/EventBus";
    import {resetUrlImproveGate, setUrlImproveGate} from '@/service/urlImproveStart';
    import EventsListenerMixin from "@/mixins/EventsListenerMixin";
    import {questionize} from "@/utils/string";
    import { Toast } from '@/model/Toast';
    import { MAX_AI_KEYWORDS, articleHasWritingContent } from '@/utils/Article';
    import {isKeywordWorkspaceTableLocked} from '@/service/keywordWorkspaceTableLock';
    import {normalizeKeywordPhrase} from '@/service/keywordInflection';
    import type {AiWritingBrief} from '@/model/AiWritingBrief';
    import {
        aiBriefFullStageKey,
        briefSelections,
        emptyAiWritingBrief,
        hasFullAiBrief,
        keepBriefSelections,
        keepPaidAiAnswers,
        mergeAiWritingBriefs,
        mergeQuestionKeywordRows,
        mergeLsiVariantRows,
        questionKeywordKey,
        looksLikeQuestionPhrase,
        partitionQuestionAndEntityPhrases,
        phraseToKeywordRow,
        shouldAutoApplyTemplate,
        factKeywordHint,
        firstBodyParagraph,
        findPhraseInArticle,
        findHeadingInArticle,
        insertPhraseIntoHtml,
        editorAnchor,
        rememberBriefValue,
        insertPhrasesIntoHtml,
        markLsiOnDensityList,
        toggleCitationUrl,
        withoutFullStageKey,
    } from '@/service/aiWritingBrief';
    import {
        markWorkedInPlatform,
        planLiveAnswerWorkIn,
        questionsFromLiveAnswers,
    } from '@/service/liveAnswerWorkIn';
    import type {GeoTemplateId} from '@/config/WritingBrief';
    import {normalizeGeoTemplate} from '@/config/WritingBrief';
    import {
        articleHasGeneratedBody,
        articleHasScrapedHeadings,
        articleWriteCtaDisabled,
        articleGenerateChoiceSettled,
        enhanceWaitingForExtraction,
        isLucyForkGenerateReady,
        lucyGenerateNowAction,
        readChatWritePath,
        isManualReEnhanceArticle,
        shouldShowKeywordWorkspaceWriteCta,
        shouldShowLucyGenerateFork,
    } from '@/utils/articleWriteCta';
    import {isJunkDensityKeyword, resolveArticleMainKeyword} from '@/service/articleMainKeyword';
    import {omitPromotedPhraseFragments} from '@/service/keywordPhraseSplit';

    export default Vue.extend({
        name: 'KeywordSelection',
        components: {
            KeywordInput,
            AIPrompt,
            KeywordsList,
            KeywordTopicChips,
            KeywordWorkspaceGroup,
            AiWritingBriefPanel,
            WorkspaceControlTip,
        },
        mixins: [EventsListenerMixin],
        props: {
            articleId: {
                type: String,
                default: ''
            },
            articleMetadata: {
                type: Article,
                default: new Article
            },
            readOnly: {
                type: Boolean,
                default: false
            },
            aiPromptMissing: {
                type: Boolean,
                default: false
            },
            isAnalyze: {
                type: Boolean,
                default: false
            }
        },
        // emits: ['input'],
        data: () => ({
            KeywordType: KeywordType,
            inspirations: {} as InspirationResponse,
            aiWritingBrief: emptyAiWritingBrief() as AiWritingBrief,
            // Bumped by every getInspirations; an answer for an older request is dropped.
            briefRequestId: 0,
            loading: false,
            loadingBrief: false,
            aiBriefError: false,
            loadingSave: false,
            selectedKeyword: '',
            aiPrompt: '',
            maxAiKeywords: 20,
            selectNewKeyword: false,
            showPromptEditor: false,
            analysisOpen: {
                related: false,
                questions: false,
                lsi: false,
                long: false,
                ac: false,
                gap: false,
            } as Record<string, boolean>,
            lucyWritePath: null as null | 'immediate' | 'customize-first',
            lucyGenerateBlock: null as null | 'too-few' | 'too-many',
            // What "Cancel this article" returns: ArticleOnCreateObserver writes it after create (#30943).
            creditUsed: null as null | ArticleCreditUsed,
            stopCreditUsedWatch: null as null | (() => void),
        }),
        computed: {
            events: function () {
                return {
                    'keywords-selection-expand': this.expand,
                    'keyword-workspace-jump': this.jumpToKeywordGroup
                };
            },
            noInspirations: function (): boolean {
                if (this.loading) {
                    return false;
                } else {
                    const related = this.getKeywords(KeywordType.RELATED) as null | Array<unknown>;
                    const questions = this.getKeywords(KeywordType.QUESTION) as null | Array<unknown>;
                    return (!Array.isArray(related) || related.length < 4) && (!Array.isArray(questions) || questions.length < 2);
                }
            },
            /**
             * A URL analysis never gets this button: with its keyword auto-saved the
             * server refuses it after a good scrape, and a failed scrape has its own
             * dialog. Nor does an article with a chosen keyword or written text: the
             * server refuses it with "Article already has keyword" (#30943).
             */
            showCancelArticle: function (): boolean {
                return this.noInspirations && !this.isAnalyze && !this.readOnly;
            },
            cancelArticleLabelKey: function (): string {
                return cancelArticleButtonKey(this.creditUsed);
            },
            articleCreatedInThisPeriod: function (): boolean {
                if (!this.articleMetadata.created_at) {
                    return true;
                }
                return this.articleMetadata.created_at + (1000 * 60 * 60 * 24 * 31) > Date.now(); // Not older than one month
            },
            lucySlotCount: function (): number {
                return countLucySlots(this.keywordsForAi);
            },
            displayMainKeyword: function (): string {
                return resolveArticleMainKeyword(this.articleMetadata);
            },
            canContinue: function (): boolean {
                return this.lucySlotCount <= MAX_AI_KEYWORDS && this.lucySlotCount > 0;
            },
            aiMeterColor: function (): string {
                if (this.lucySlotCount <= this.maxAiKeywords && this.lucySlotCount > 0) {
                    return 'accent';
                }
                return 'warning';
            },
            hasGeneratedBody: function (): boolean {
                return articleHasGeneratedBody(this.articleMetadata);
            },
            showLucyFork: function (): boolean {
                return shouldShowLucyGenerateFork({
                    disableSelection: this.disableSelection,
                    lucyWritePath: this.lucyWritePath,
                    enhanced: this.articleMetadata.enhanced,
                    aiGenerateStatus: this.articleMetadata.aiGenerateStatus,
                    isAnalyze: !!this.articleMetadata.isAnalyze,
                    hasGeneratedBody: this.hasGeneratedBody,
                });
            },
            showLucyFastSummary: function (): boolean {
                return !this.disableSelection && this.lucyWritePath === 'immediate';
            },
            lucyForkImmediateReady: function (): boolean {
                return isLucyForkGenerateReady({
                    analysisLoading: this.loading,
                    briefLoading: this.loadingBrief,
                    isAnalyze: !!this.isAnalyze,
                    scrapeLoading: !!this.$store.getters.articleAnalyzeScrapeLoading,
                    scrapedHeadingsSaved: articleHasScrapedHeadings(this.articleMetadata),
                });
            },
            lucyForkImmediateLoading: function (): boolean {
                return this.loadingSave || !this.lucyForkImmediateReady;
            },
            lucyGenerateBlockMessage: function (): string {
                if (this.lucyGenerateBlock === 'too-few') {
                    return String(this.$t('keywords.lucy.generateNowNeedMore', [this.lucySlotCount]));
                }
                if (this.lucyGenerateBlock === 'too-many') {
                    return String(this.$t('keywords.lucy.writeDisabledOver', [this.lucySlotCount]));
                }
                return '';
            },
            lucyForkTitle(): string {
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.forkTitleImprove'))
                    : String(this.$t('keywords.lucy.forkTitle'));
            },
            lucyForkLead(): string {
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.forkLeadImprove'))
                    : String(this.$t('keywords.lucy.forkLead'));
            },
            lucyForkImmediateLabel(): string {
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.improveNow'))
                    : String(this.$t('keywords.lucy.generateNow'));
            },
            lucyForkImmediateLine(): string {
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.improveNowLine'))
                    : String(this.$t('keywords.lucy.generateNowLine'));
            },
            lucyForkImmediateTip(): string {
                if (!this.lucyForkImmediateReady) {
                    return String(this.$t('keywords.lucy.tip.forkWaiting'));
                }
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.tip.forkImmediateImprove'))
                    : String(this.$t('keywords.lucy.tip.forkImmediate'));
            },
            lucyForkCustomizeLabel(): string {
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.customizeBeforeImprove'))
                    : String(this.$t('keywords.lucy.customizeFirst'));
            },
            lucyForkCustomizeLine(): string {
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.customizeBeforeImproveLine'))
                    : String(this.$t('keywords.lucy.customizeFirstLine'));
            },
            lucyForkCustomizeTip(): string {
                return this.isAnalyze
                    ? String(this.$t('keywords.lucy.tip.forkCustomizeImprove'))
                    : String(this.$t('keywords.lucy.tip.forkCustomize'));
            },
            lucyLimitMessage: function (): string {
                if (this.lucySlotCount === 0) {
                    return String(this.$t('keywords.lucy.limitEmpty'));
                }
                if (this.lucySlotCount > this.maxAiKeywords) {
                    return String(this.$t('keywords.lucy.limitOver'));
                }
                if (this.lucySlotCount === this.maxAiKeywords) {
                    return String(this.$t('keywords.lucy.limitFull'));
                }
                return String(this.$t('keywords.lucy.limitOk', [this.maxAiKeywords - this.lucySlotCount]));
            },
            lucyLimitMessageClass: function (): string {
                if (this.lucySlotCount > this.maxAiKeywords) {
                    return 'warning--text';
                }
                if (this.lucySlotCount === this.maxAiKeywords) {
                    return 'primary--text';
                }
                return 'text--secondary';
            },
            enhancingOverride: function(): boolean {
              // Lock Lucy tables only while enhance runs or after it finished — not during URL-improve prep.
              return this.articleMetadata.isAnalyze
                  && (this.articleMetadata.enhanced === 'processing' || this.articleMetadata.enhanced === 'done');
            },
            keywordSelectionShown: {
                get(): boolean {
                    return this.$store.getters.keywordSelectionShown;
                },
                set(value: boolean) {
                    this.$store.commit('setKeywordSelectionShown', value);
                }
            },
            disableSelection: function(): boolean {
                return isKeywordWorkspaceTableLocked({
                    sharePreview: !!this.$store.getters.sharePreview,
                    enhancingOverride: this.enhancingOverride,
                    articleMetadata: this.articleMetadata,
                    keywordSelectionShown: this.keywordSelectionShown,
                    readOnly: this.readOnly,
                });
            },
            showAnalysisToggle: function(): boolean {
              if (this.articleMetadata.enhanced === 'processing') {
                  return false;
              }
              return this.readOnly || this.disableSelection || this.isAnalyze;
            },
            articleWriteFinished: function(): boolean {
                return articleGenerateChoiceSettled({
                    enhanced: this.articleMetadata.enhanced,
                    aiGenerateStatus: this.articleMetadata.aiGenerateStatus,
                    isAnalyze: !!this.articleMetadata.isAnalyze,
                    hasGeneratedBody: this.hasGeneratedBody,
                });
            },
            manualReEnhanceCta: function (): boolean {
                return isManualReEnhanceArticle({
                    isAnalyze: !!this.isAnalyze,
                    enhanced: this.articleMetadata.enhanced,
                    aiGenerateStatus: this.articleMetadata.aiGenerateStatus,
                });
            },
            showPrimaryWriteAction: function(): boolean {
                return shouldShowKeywordWorkspaceWriteCta({
                    sharePreview: !!this.$store.getters.sharePreview,
                    isAnalyze: this.isAnalyze,
                    enhanced: this.articleMetadata.enhanced,
                    aiGenerateStatus: this.articleMetadata.aiGenerateStatus,
                    readOnly: this.readOnly,
                    selectedKeyword: this.selectedKeyword,
                    hasGeneratedBody: this.hasGeneratedBody,
                    showLucyFork: this.showLucyFork,
                    keywordSelectionShown: this.keywordSelectionShown,
                    lucySlotCount: this.lucySlotCount,
                });
            },
            enhanceWaitingForPage: function (): boolean {
                return enhanceWaitingForExtraction({
                    isAnalyze: !!this.isAnalyze,
                    scrapeLoading: !!this.$store.getters.articleAnalyzeScrapeLoading,
                });
            },
            writeCtaDisabled: function(): boolean {
                if (this.enhanceWaitingForPage) {
                    return true;
                }
                return articleWriteCtaDisabled({
                    selectedKeyword: this.selectedKeyword,
                    tooManyKeywords: this.lucySlotCount > MAX_AI_KEYWORDS,
                    selectNewKeyword: this.selectNewKeyword,
                    lucySlotCount: this.lucySlotCount,
                });
            },
            writeCtaTooltip: function(): string {
                if (this.enhanceWaitingForPage) {
                    return String(this.$t('article.manualEnhance.loadingPage'));
                }
                if (!this.writeCtaDisabled || this.selectNewKeyword || !this.selectedKeyword) {
                    return '';
                }
                if (this.lucySlotCount === 0) {
                    return String(this.$t('keywords.lucy.writeDisabledEmpty'));
                }
                if (this.lucySlotCount > MAX_AI_KEYWORDS) {
                    return String(this.$t('keywords.lucy.writeDisabledOver', [this.lucySlotCount]));
                }
                return String(this.$t('keywords.info.tooManyKeywordsForAi'));
            },
            primaryWriteLabel: function(): string {
                if (this.isAnalyze || this.manualReEnhanceCta) {
                    return String(this.$t('article.manualEnhance.enhance'));
                }
                return String(this.$t('keywords.generateInspiration'));
            },
            briefHasContent: function(): boolean {
                const brief = this.aiWritingBrief;
                return Boolean(
                    brief && (
                        (brief.questions && brief.questions.length)
                        || (brief.answers && brief.answers.length)
                        || (brief.entities && brief.entities.length)
                        || (brief.mustCoverHeadings && brief.mustCoverHeadings.length)
                        || brief.intent
                    )
                );
            },
            promptRequired: function(): boolean {
                return !this.readOnly && !this.articleMetadata.aiPrompt && !this.articleMetadata.noAiPrompt;
            },
            existingKeywordKeys: function(): Set<string> {
                const keys = new Set<string>();
                const bags = [
                    this.inspirations.related,
                    this.inspirations.questions,
                    this.inspirations.ideas,
                    this.inspirations.suggestions,
                ];
                for (const rows of bags) {
                    for (const row of (rows as Array<{keyword?: string}> || [])) {
                        const key = questionKeywordKey(String(row.keyword || ''));
                        if (key) {
                            keys.add(key);
                        }
                    }
                }
                return keys;
            },
            visiblePeopleAlsoSearch: function(): string[] {
                const main = resolveArticleMainKeyword(this.articleMetadata);
                return omitPromotedPhraseFragments(
                    (this.aiWritingBrief.peopleAlsoSearch || []).filter((item) => {
                        const key = questionKeywordKey(item);
                        return key && !this.existingKeywordKeys.has(key);
                    }),
                    main,
                    (item) => item
                );
            },
            visibleAutocomplete: function(): string[] {
                const main = resolveArticleMainKeyword(this.articleMetadata);
                return omitPromotedPhraseFragments(
                    (this.aiWritingBrief.autocomplete || []).filter((item) => {
                        const key = questionKeywordKey(item);
                        return key && !this.existingKeywordKeys.has(key);
                    }),
                    main,
                    (item) => item
                );
            },
            autocompleteQuestionPhrases: function(): string[] {
                return this.visibleAutocomplete.filter((item) => looksLikeQuestionPhrase(item));
            },
            autocompletePhraseItems: function(): Array<{keyword: string}> {
                return this.visibleAutocomplete
                    .filter((item) => !looksLikeQuestionPhrase(item))
                    .map((keyword) => ({keyword}));
            },
            relatedKeywords: function(): Array<Record<string, unknown>> {
                const rows = [...((this.inspirations.related as unknown as Array<Record<string, unknown>> | undefined) || [])];
                const keys = new Set(rows.map((row) => questionKeywordKey(String(row.keyword || ''))));
                for (const phrase of this.visiblePeopleAlsoSearch) {
                    const key = questionKeywordKey(phrase);
                    if (!key || keys.has(key)) {
                        continue;
                    }
                    keys.add(key);
                    rows.push(phraseToKeywordRow(phrase, 'pas'));
                }
                return omitPromotedPhraseFragments(rows, resolveArticleMainKeyword(this.articleMetadata), (row) => row.keyword);
            },
            questionKeywords: function(): Array<Record<string, unknown>> {
                const rows = ((this.inspirations.questions as unknown as Array<Record<string, unknown>> | undefined) || [])
                    .filter((row) => looksLikeQuestionPhrase(String(row.keyword || '')));
                return omitPromotedPhraseFragments(rows, resolveArticleMainKeyword(this.articleMetadata), (row) => row.keyword);
            },
            variationKeywords: function(): Array<Record<string, unknown>> {
                const rows = (this.inspirations.ideas as unknown as Array<Record<string, unknown>> | undefined) || [];
                return omitPromotedPhraseFragments(rows, resolveArticleMainKeyword(this.articleMetadata), (row) => row.keyword);
            },
            longTailKeywords: function(): Array<Record<string, unknown>> {
                const rows = (this.inspirations.suggestions as unknown as Array<Record<string, unknown>> | undefined) || [];
                return omitPromotedPhraseFragments(rows, resolveArticleMainKeyword(this.articleMetadata), (row) => row.keyword);
            },
            gapTopicItems: function(): Array<{keyword: string; searchVolume?: number | null; keywordDifficulty?: number | null}> {
                if (!this.articleMetadata.url) {
                    return [];
                }
                const rows = (this.aiWritingBrief.gapTopics || []).filter((item) => {
                    const key = questionKeywordKey(item.keyword);
                    return key && !this.existingKeywordKeys.has(key);
                });
                return omitPromotedPhraseFragments(rows, resolveArticleMainKeyword(this.articleMetadata), (row) => row.keyword);
            },
            showGapSkeleton: function(): boolean {
                return this.loadingBrief && this.isAnalyze && !!this.articleMetadata.url && !this.gapTopicItems.length;
            },
            keywordsForAi: {
                get(): KeywordForAi[] {
                    return this.$store.getters.articleKeywordsForAi;
                },
                set(value: KeywordForAi[]) {
                    this.$store.commit('setKeywordsForAi', value);
                }
            },
        },
        watch: {
            showCancelArticle: {
                immediate: true,
                handler: function (shown: boolean): void {
                    this.watchCreditUsed(shown);
                },
            },
            loading: function (): void {
                this.publishUrlImproveGate();
            },
            loadingBrief: function (): void {
                this.publishUrlImproveGate();
            },
            lucyWritePath: function (): void {
                this.publishUrlImproveGate();
            },
            'articleMetadata.aiPrompt': function (value: string): void {
                if (value !== this.aiPrompt) {
                    this.aiPrompt = value || '';
                }
            },
            aiPrompt: function (value: string): void {
                if (this.readOnly || this.$store.getters.sharePreview) {
                    return;
                }
                if (value !== this.articleMetadata.aiPrompt) {
                    this.$set(this.articleMetadata, 'aiPrompt', value || '');
                    this.$set(this.articleMetadata, 'noAiPrompt', !value);
                }
            },
        },
        created: function () {
            if (!this.$store.getters.sharePreview) {
                this.getInspirations({keepSavedAnswers: true});
            } else {
                this.loading = false;
                this.loadingBrief = false;
            }
            this.aiPrompt = this.articleMetadata.aiPrompt || '';
            this.selectedKeyword = this.articleMetadata.selectedKeyword || this.articleMetadata.keyword || '';
            // Written articles keep the store flag (user may have opened analysis).
            // New briefs still open the picker. URL-analyze keeps analysis collapsed.
            // Never clear selectedKeyword here.
            if (this.isAnalyze) {
                this.keywordSelectionShown = false;
            } else if (!this.readOnly && !articleHasWritingContent(this.articleMetadata)) {
                this.keywordSelectionShown = false;
            }
            const savedForAi = this.articleMetadata.keywordsForAi;
            if (Array.isArray(savedForAi) && savedForAi.length > 0) {
                this.keywordsForAi = savedForAi;
            } else if (!this.readOnly) {
                this.keywordsForAi = [];
            }
            if (this.articleMetadata.aiWritingBrief) {
                this.aiWritingBrief = this.articleMetadata.aiWritingBrief;
            }
            EventBus.$on('update-article-keywords-for-ai', this.updateArticleKeywordsForAi);
            EventBus.$on('collapse-keyword-analysis', this.collapseKeywordAnalysis);
            if (!this.$store.getters.sharePreview && this.isAnalyze && !this.articleMetadata.selectedKeyword) {
              // New URL analysis: skip straight to extraction. Keep the generate
              // buttons visible when the user later opens keyword analysis.
              this.saveSelectedKeyword(this.articleMetadata.keyword);
            }
            this.publishUrlImproveGate();
            this.applyChatWritePath();
        },
        mounted: function () {
            if (this.lucyWritePath !== 'customize-first' || this.$store.getters.sharePreview) {
                return;
            }
            this.$nextTick(() => {
                const panel = this.$refs.aiWritingBriefPanel as {expandBriefLayers?: () => void} | undefined;
                panel?.expandBriefLayers?.();
                scrollIntoViewIfNotVisible('keywordTables');
            });
        },
        beforeDestroy: function () {
            EventBus.$off('update-article-keywords-for-ai');
            EventBus.$off('collapse-keyword-analysis', this.collapseKeywordAnalysis);
            resetUrlImproveGate();
            this.watchCreditUsed(false);
        },
        methods: {
            watchCreditUsed: function (on: boolean): void {
                if (this.stopCreditUsedWatch) {
                    this.stopCreditUsedWatch();
                    this.stopCreditUsedWatch = null;
                }
                this.creditUsed = null;
                if (!on || this.$store.getters.sharePreview || !this.articleId) {
                    return;
                }
                this.stopCreditUsedWatch = Firestore.createArticleCreditUsedWatcher(this.articleId, (creditUsed) => {
                    this.creditUsed = creditUsed;
                });
            },
            /**
             * persistBrief: save the article once each stage arrives (an explicit user action).
             * keepSavedAnswers: the brief held is the article's saved brief for the current main
             * keyword, so the fast stage keeps its paid AI answers.
             */
            getInspirations: function ({persistBrief = false, keepSavedAnswers = false} = {}) {
                const requestId = ++this.briefRequestId;
                const request = {
                    keyword: this.articleMetadata.keyword,
                    language_code: this.articleMetadata.language_code,
                    location_code: this.articleMetadata.location_code,
                    url: this.articleMetadata.url || undefined,
                    geoTemplate: this.articleMetadata.geoTemplate,
                };
                // The full brief (paid AI answers) is fetched once per article and main keyword.
                // Reopening an article whose saved brief already has it does not fetch the brief again.
                const briefSaved = hasFullAiBrief(this.articleMetadata.aiWritingBrief, request);
                this.loading = true;
                this.loadingBrief = !briefSaved;
                this.aiBriefError = false;
                Functions.D4SGetInspiration({
                    keyword: request.keyword,
                    language_code: request.language_code,
                    location_code: request.location_code,
                }).then(response => {
                    this.inspirations = response;
                    if (this.inspirations.related) {
                        // @ts-ignore
                        this.inspirations.related = this.inspirations.related.map((relatedKeyword: Record<string, unknown>) => relatedKeyword.keyword_data);
                    }
                    this.inspirations.questions = this.inspirations.questions?.map((question: Keyword) => {
                        question.keyword = questionize(question.keyword, this.articleMetadata.language_code);
                        return question;
                    });
                    this.applyBriefToQuestions();
                    this.applyBriefToVariants();
                    this.pickDefaultKeywords();
                    this.mergeSecondaryKeywords();
                    this.applyDefaultCitations();
                    return response;
                }).catch(error => {
                    window.console.warn('Inspiration fetch failed', error);
                }).finally(() => {
                    this.loading = false;
                });
                if (briefSaved) {
                    return;
                }
                const fullStageKey = aiBriefFullStageKey(request);
                // A brief requested for an earlier main keyword (the full stage takes minutes)
                // must not land in, or stamp, the brief of the current one (#30945).
                const isCurrent = () => requestId === this.briefRequestId
                    && aiBriefFullStageKey(this.articleMetadata) === fullStageKey;
                Functions.D4SGetAiWritingBrief({...request, stage: 'fast'})
                    .then((brief) => {
                        if (!isCurrent()) {
                            return;
                        }
                        this.aiBriefError = false;
                        // The fast stage has no paid AI answers. Replacing a saved brief with it (a
                        // brief from before the marker, or one for an older url) would lose them
                        // whenever the full stage then fails, e.g. on the daily limit (429), and the
                        // next save of the article would drop them for good (#30945).
                        this.setAiWritingBrief(
                            keepSavedAnswers ? keepPaidAiAnswers(this.aiWritingBrief, brief) : brief,
                            false,
                            persistBrief
                        );
                    })
                    .catch(() => {
                        if (isCurrent()) {
                            this.aiBriefError = !this.briefHasContent;
                        }
                    })
                    .finally(() => {
                        if (!isCurrent()) {
                            return;
                        }
                        // Keep the panel in a waiting state until the slower
                        // full fetch settles. Fast questions often arrive first;
                        // empty LSI / shared-topic columns are still loading.
                        Functions.D4SGetAiWritingBrief({...request, stage: 'full'})
                            .then((brief) => {
                                if (!isCurrent()) {
                                    return;
                                }
                                this.aiBriefError = false;
                                // A source that failed is fetched again on the next open;
                                // only a brief without failures counts as full (#30945).
                                const {incompleteSources, ...fullBrief} = brief;
                                this.setAiWritingBrief(
                                    incompleteSources?.length ? fullBrief : {...fullBrief, fullStageKey},
                                    true,
                                    persistBrief
                                );
                            })
                            .catch(() => {
                                if (isCurrent()) {
                                    this.aiBriefError = !this.briefHasContent;
                                }
                            })
                            .finally(() => {
                                if (isCurrent()) {
                                    this.loadingBrief = false;
                                }
                            });
                    });
            },
            setAiWritingBrief: function (brief: AiWritingBrief, merge = false, persist = false): void {
                this.aiWritingBrief = merge
                    ? mergeAiWritingBriefs(this.aiWritingBrief, brief)
                    : keepBriefSelections(this.aiWritingBrief, brief);
                const fromAnswers = questionsFromLiveAnswers(this.aiWritingBrief?.answers);
                if (fromAnswers.length) {
                    const existing = this.aiWritingBrief.questions || [];
                    const extra = fromAnswers.filter((item) =>
                        !existing.some((row) =>
                            questionKeywordKey(row.question) === questionKeywordKey(item.question)
                        )
                    );
                    if (extra.length) {
                        this.aiWritingBrief = {
                            ...this.aiWritingBrief,
                            questions: [...existing, ...extra].slice(0, 24),
                        };
                    }
                }
                this.$set(this.articleMetadata, 'aiWritingBrief', this.aiWritingBrief);
                this.applyBriefToQuestions();
                this.applyBriefToVariants();
                if (shouldAutoApplyTemplate(
                    this.articleMetadata.geoTemplate,
                    this.aiWritingBrief.suggestedGeoTemplate,
                    this.articleMetadata.geoTemplateUserPicked
                )) {
                    this.applySuggestedTemplate(this.aiWritingBrief.suggestedGeoTemplate as GeoTemplateId, persist);
                }
                if (persist && merge && this.articleMetadata.id) {
                    Firestore.saveUserArticle(this.articleMetadata as Article).catch(() => undefined);
                }
                if (!this.articleMetadata.keywordsForAi || this.articleMetadata.keywordsForAi.length === 0) {
                    this.pickDefaultKeywords(true);
                }
                this.applyDefaultCitations();
            },
            applyBriefToQuestions: function (): void {
                const fromBrief = partitionQuestionAndEntityPhrases(this.aiWritingBrief?.questions || []);
                const fromAnswers = partitionQuestionAndEntityPhrases(questionsFromLiveAnswers(this.aiWritingBrief?.answers));
                const autocompleteQuestions = this.autocompleteQuestionPhrases.map((question) => ({
                    question,
                    source: 'autocomplete',
                }));
                const labs = ((this.inspirations.questions as unknown as Array<Record<string, unknown>> | undefined) || [])
                    .filter((row) => looksLikeQuestionPhrase(String(row.keyword || '')));
                this.inspirations.questions = mergeQuestionKeywordRows(
                    labs,
                    [...fromBrief.questions, ...fromAnswers.questions, ...autocompleteQuestions],
                    this.articleMetadata.language_code
                ) as unknown as Keyword[];
                const leakedEntities = [
                    ...fromBrief.entities,
                    ...fromAnswers.entities,
                    ...(((this.inspirations.questions as unknown as Array<Record<string, unknown>> | undefined) || [])
                        .filter((row) => !looksLikeQuestionPhrase(String(row.keyword || '')))
                        .map((row) => String(row.keyword || ''))),
                ];
                if (leakedEntities.length) {
                    this.inspirations.ideas = mergeLsiVariantRows(
                        this.inspirations.ideas as unknown as Array<Record<string, unknown>>,
                        leakedEntities
                    ) as unknown as Keyword[];
                }
            },
            applyBriefToVariants: function (): void {
                const fromBrief = partitionQuestionAndEntityPhrases(this.aiWritingBrief?.questions || []);
                const fromAnswers = partitionQuestionAndEntityPhrases(questionsFromLiveAnswers(this.aiWritingBrief?.answers));
                this.inspirations.ideas = mergeLsiVariantRows(
                    this.inspirations.ideas as unknown as Array<Record<string, unknown>>,
                    [
                        ...(this.aiWritingBrief?.entities || []),
                        ...fromBrief.entities,
                        ...fromAnswers.entities,
                    ]
                ) as unknown as Keyword[];
            },
            pickDefaultKeywords: function (keepExisting = false): void {
                const userMain = resolveArticleMainKeyword(this.articleMetadata);
                if (userMain) {
                    this.selectedKeyword = userMain;
                }
                if (this.articleMetadata.keywordsForAi !== null && this.articleMetadata.keywordsForAi.length > 0) {
                    this.fixKeywordType();
                    this.keywordsForAi = this.articleMetadata.keywordsForAi;
                    return;
                }
                if (keepExisting && this.keywordsForAi.length > 0) {
                    return;
                }
                this.keywordsForAi = buildDefaultKeywordsForAi({
                    mainKeyword: userMain || this.selectedKeyword,
                    relatedRows: this.relatedKeywords,
                    questionRows: this.questionKeywords,
                });
            },
            applyDefaultCitations: function (): void {
                const urls = pickDefaultCitationUrls(
                    this.aiWritingBrief.citedSources || [],
                    this.aiWritingBrief.selectedCitationUrls
                );
                if (!urls.length) {
                    return;
                }
                this.$set(this.aiWritingBrief, 'selectedCitationUrls', urls);
            },
            applyChatWritePath: function (): void {
                if (this.$store.getters.sharePreview) {
                    return;
                }
                if (articleGenerateChoiceSettled({
                    enhanced: this.articleMetadata.enhanced,
                    aiGenerateStatus: this.articleMetadata.aiGenerateStatus,
                    isAnalyze: !!this.articleMetadata.isAnalyze,
                    hasGeneratedBody: this.hasGeneratedBody,
                })) {
                    return;
                }
                const query = this.$route && this.$route.query ? this.$route.query.cesta : null;
                const path = readChatWritePath(query);
                if (path === 'immediate') {
                    this.lucyWritePath = 'immediate';
                    this.keywordSelectionShown = false;
                    return;
                }
                if (path === 'customize-first') {
                    this.onUpravitPredGenerovanim();
                }
            },
            onGenerovatHned: function (): void {
                // Disabled + loader until ready. Do not queue the click for later.
                if (!this.lucyForkImmediateReady) {
                    return;
                }
                const action = lucyGenerateNowAction(this.lucySlotCount, this.maxAiKeywords);
                if (action !== 'start') {
                    this.lucyGenerateBlock = action;
                    return;
                }
                this.lucyGenerateBlock = null;
                this.lucyWritePath = 'immediate';
                this.keywordSelectionShown = false;
                if (this.isAnalyze) {
                    // Vylepšit hned only selects the path. Writing starts on
                    // Vylepšit článek / Generovat SEO článek.
                    return;
                }
                // New article: same entry as Generovat SEO článek (mode whole).
                this.saveSelectedKeyword(undefined, true);
            },
            onUpravitPredGenerovanim: function (): void {
                this.lucyWritePath = 'customize-first';
                this.keywordSelectionShown = true;
                this.analysisOpen = {
                    related: true,
                    questions: true,
                    lsi: false,
                    long: true,
                    ac: false,
                    gap: false,
                };
                const panel = this.$refs.aiWritingBriefPanel as {expandBriefLayers?: () => void} | undefined;
                panel?.expandBriefLayers?.();
                this.$nextTick(() => {
                    scrollIntoViewIfNotVisible('keywordTables');
                });
            },
            getKeywords: function (type: number) {
                switch (type) {
                case KeywordType.VARIATION:
                    return this.variationKeywords;
                case KeywordType.QUESTION:
                    return this.questionKeywords;
                case KeywordType.LONG_TAIL:
                    return this.longTailKeywords;
                case KeywordType.RELATED:
                    return this.relatedKeywords;
                default:
                    return [];
                }
            },
            fixKeywordType: function () {
                // Sometimes a type 0 (related) was wrongly assigned to the keywordForAi
                if (this.articleMetadata.keywordsForAi !== null) {
                    this.articleMetadata.keywordsForAi.forEach((kfa: KeywordForAi) => {
                        for (const type of Object.values(KeywordType)) {
                            // @ts-ignore
                            if ((this.getKeywords(type)|| []).some(d4sdata => d4sdata.keyword === kfa.keyword)) {
                                kfa.type = type;
                            }
                        }
                    });
                }
            },
            mergeSecondaryKeywords: function (): void {
                const main = resolveArticleMainKeyword({
                    keyword: this.articleMetadata.keyword,
                    selectedKeyword: this.selectedKeyword,
                });
                const extras = (this.articleMetadata.secondaryKeywords || [])
                    .map((item: string) => String(item || '').replace(/\s+/g, ' ').trim())
                    .filter((item: string) => item
                        && normalizeKeywordPhrase(item) !== normalizeKeywordPhrase(main)
                        && !isJunkDensityKeyword(item, main));
                const current = this.articleMetadata.secondaryKeywords || [];
                if (extras.length !== current.length || extras.some((item, index) => item !== current[index])) {
                    this.$set(this.articleMetadata, 'secondaryKeywords', extras);
                }
                extras.forEach((keyword: string) => {
                    if (!this.keywordsForAi.some((item) => item.keyword === keyword)) {
                        this.keywordsForAi.unshift({
                            keyword,
                            type: KeywordType.RELATED,
                            intent: 'weave',
                        });
                    }
                });
            },
            collapseKeywordAnalysis: function (): void {
                this.keywordSelectionShown = false;
                Object.keys(this.analysisOpen).forEach((key) => {
                    this.$set(this.analysisOpen, key, false);
                });
                const panel = this.$refs.aiWritingBriefPanel as {collapseBriefLayers?: () => void} | undefined;
                panel?.collapseBriefLayers?.();
            },
            publishUrlImproveGate: function (): void {
                if (!this.isAnalyze) {
                    return;
                }
                setUrlImproveGate({
                    analysisLoading: !!this.loading,
                    briefLoading: !!this.loadingBrief,
                    customizeFirst: this.lucyWritePath === 'customize-first',
                });
            },
            saveSelectedKeyword: function (override?: string, userConfirmed = false): void {
                if (userConfirmed && this.enhanceWaitingForPage) {
                    return;
                }
                if (override) {
                    this.selectedKeyword = override;
                }
                this.$set(this.articleMetadata, 'aiWritingBrief', this.aiWritingBrief);
                collapseAnalysisAndScrollToHBlocks();
                this.loadingSave = true;
                let questions = [] as Partial<ArticleQuestion>[];
                if (this.inspirations.questions) {
                    // save only questions which were selected for AI, otherwise there would be questions with no answer generated
                    questions = this.keywordsForAi.filter(aiKeyword => aiKeyword.type === KeywordType.QUESTION).map(aiKeyword => ({
                        question: aiKeyword.keyword,
                        answer: ''
                    }));
                }
                this.$emit('input', this.selectedKeyword, this.aiPrompt, questions, this.keywordsForAi, {userConfirmed});
                this.$nextTick(() => {
                    this.loadingSave = false;
                });
            },
            deleteArticleAndRefundCredit: function () {
                this.loadingSave = true;
                // force: a failed scrape gets back the extra or free credit it
                // took, not only the plan slot. The server still decides (#30943).
                Functions.CancelArticle({articleId: this.articleId, force: true}).then(() => {
                    this.$router.push('/');
                }).catch(error => {
                    this.$store.dispatch("handleApiError", error);
                }).finally(() => {
                    this.loadingSave = false;
                });
            },
            expand: function () {
                this.openKeywordAnalysisWorkspace();
            },
            openKeywordAnalysisWorkspace: function (): void {
                this.keywordSelectionShown = true;
                this.analysisOpen.related = true;
                this.analysisOpen.questions = true;
                this.$nextTick(() => {
                    const el = this.$refs.keywordTablesAnchor as HTMLElement | undefined;
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({behavior: 'smooth', block: 'start'});
                    }
                });
            },
            onPrimaryWriteClick: function (): void {
                if (this.manualReEnhanceCta) {
                    EventBus.$emit('request-enhance');
                    return;
                }
                this.saveSelectedKeyword(undefined, true);
            },
            toggleKeywordAnalysis: function () {
                const opening = !this.keywordSelectionShown;
                this.keywordSelectionShown = opening;
                if (opening) {
                    this.analysisOpen.related = true;
                    this.analysisOpen.questions = true;
                    this.$nextTick(() => {
                        const el = this.$refs.keywordTablesAnchor as HTMLElement | undefined;
                        if (el && typeof el.scrollIntoView === 'function') {
                            el.scrollIntoView({behavior: 'smooth', block: 'start'});
                        }
                    });
                }
            },
            toggleAnalysisGroup: function (id: string): void {
                this.$set(this.analysisOpen, id, !this.analysisOpen[id]);
            },
            groupCountLabel: function (count: number): string {
                return count > 10 ? count + ' · scroll 10' : String(count);
            },
            jumpToKeywordGroup: function (groupId: string): void {
                const key = String(groupId || '').replace(/^g-/, '');
                this.keywordSelectionShown = true;
                if (key && Object.prototype.hasOwnProperty.call(this.analysisOpen, key)) {
                    this.$set(this.analysisOpen, key, true);
                }
                this.$nextTick(() => {
                    const el = document.getElementById(groupId) || (this.$refs.keywordTablesAnchor as HTMLElement | undefined);
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({behavior: 'smooth', block: 'start'});
                    }
                });
            },
            openKeywordAnalysis: function () {
                this.keywordSelectionShown = true;
                this.$nextTick(() => {
                    const el = this.$refs.keywordTablesAnchor as HTMLElement | undefined;
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({behavior: 'smooth', block: 'start'});
                    }
                });
            },
            toggleNewKeywordSelect: function () {
                this.selectNewKeyword = !this.selectNewKeyword;
                this.$nextTick(() => {
                    scrollIntoViewIfNotVisible('#keywordSelectionAfter');
                });
            },
            closeKeywordAnalysis: function () {
                this.selectNewKeyword = false;
                this.keywordSelectionShown = false;
                this.$nextTick(() => {
                    const article = document.getElementById('articleCreationContainer');
                    if (article) {
                        article.scrollIntoView({behavior: 'smooth', block: 'start'});
                    }
                });
            },
            changeMainKeyword: function (newKeyword: string): void {
                const initialKeyword = this.articleMetadata.keyword;
                this.loading = true;
                if (!this.articleMetadata.keywordChangeCounter) {
                    // eslint-disable-next-line vue/no-mutating-props
                    this.articleMetadata.keywordChangeCounter = 0;
                }
                this.$set(this.articleMetadata, 'keywordChangeCounter', this.articleMetadata.keywordChangeCounter + 1);
                this.$set(this.articleMetadata, 'keyword', newKeyword);
                Firestore.saveUserArticle(this.articleMetadata).then(() => {
                    // reset keywords selected for AI
                    this.keywordsForAi = [];
                    // the saved full brief belonged to the old keyword
                    this.aiWritingBrief = {...withoutFullStageKey(this.aiWritingBrief), ...briefSelections(null)};
                    // generate new inspirations
                    this.getInspirations({persistBrief: true});
                    // refresh article list in left menu
                    EventBus.$emit('reload-article-list');
                    scrollTo({top: 0, behavior: 'smooth'});
                }).catch(error => {
                    // keyword change failed
                    this.$set(this.articleMetadata, 'keyword', initialKeyword);
                    this.$set(this.articleMetadata, 'keywordChangeCounter', this.articleMetadata.keywordChangeCounter - 1);
                    console.error(error);
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'unexpectedError',
                    } as Toast)
                    this.loading = false;
                });
            },
            onKeywordInput: function (keywordForm: ArticleMetadata) {
                this.selectNewKeyword = false;
                this.changeMainKeyword(keywordForm.keyword);
            },
            updateArticleKeywordsForAi: function () {
              this.$emit('updateArticleKeywordsForAi', this.keywordsForAi);
            },
            applySuggestedTemplate: function (template: GeoTemplateId, persist = true): void {
                if (!template || this.disableSelection) {
                    return;
                }
                this.$set(this.articleMetadata, 'geoTemplate', normalizeGeoTemplate(template));
                if (persist && this.articleMetadata.id) {
                    Firestore.saveUserArticle(this.articleMetadata as Article).catch(() => undefined);
                }
            },
            persistBrief(): void {
                this.$set(this.articleMetadata, 'aiWritingBrief', this.aiWritingBrief);
                if (this.articleMetadata.id) {
                    Firestore.saveUserArticle(this.articleMetadata as Article).catch(() => undefined);
                }
            },
            toastBrief(title: string): void {
                this.$store.dispatch('toasts/add', {
                    type: 'success',
                    title,
                } as Toast);
            },
            ensureContentBlock(contentPart: 'h1' | 'h2', index: number): void {
                const article = this.$store.getters.article as Article;
                const blocks = article[contentPart] || [];
                while (blocks.length <= index) {
                    this.$store.commit('insertArticleContentSubPart', {
                        contentPart,
                        index: blocks.length,
                        keepContent: false,
                        aiLoading: false,
                    });
                }
            },
            jumpTo(target: {contentPart: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'; index: number; tag: 'h' | 'p'}): void {
                this.$nextTick(() => {
                    focusWorkspaceAnchor(editorAnchor(target));
                });
            },
            rememberFact(sentence: string): void {
                this.aiWritingBrief = rememberBriefValue(this.aiWritingBrief, 'selectedFacts', sentence);
                const hint = factKeywordHint(sentence);
                if (hint && this.keywordsForAi.length < this.maxAiKeywords
                    && !this.keywordsForAi.some((item) => item.keyword.toLowerCase() === hint.toLowerCase())) {
                    this.keywordsForAi = [
                        ...this.keywordsForAi,
                        {keyword: hint, type: KeywordType.RELATED}
                    ];
                }
                this.persistBrief();
            },
            insertBriefLsi(phrases: string[]): void {
                if (this.$store.getters.sharePreview || !phrases || !phrases.length) {
                    return;
                }
                const article = this.$store.getters.article as Article;
                const locale = this.$i18n.locale === 'cs' ? 'cs' : 'en';
                const missing = phrases.filter((item) => !findPhraseInArticle(article, item));
                if (!missing.length) {
                    this.toastBrief('aiBrief.nothingToInsert');
                    return;
                }
                const target = firstBodyParagraph(article);
                this.ensureContentBlock(target.contentPart as 'h1' | 'h2', target.index);
                const current = (this.$store.getters.article as Article)[target.contentPart][target.index]?.p || '';
                const result = insertPhrasesIntoHtml(current, missing, 'lsi', locale);
                this.$store.commit('setArticleContent', {
                    contentPart: target.contentPart,
                    index: target.index,
                    contentKey: 'p',
                    value: result.html,
                });
                markLsiOnDensityList(this.$store.getters.article as Article, result.inserted);
                result.inserted.forEach((phrase) => {
                    this.aiWritingBrief = rememberBriefValue(this.aiWritingBrief, 'selectedFacts', phrase);
                    if (this.keywordsForAi.length < this.maxAiKeywords
                        && !this.keywordsForAi.some((item) => item.keyword.toLowerCase() === phrase.toLowerCase())) {
                        this.keywordsForAi = [
                            ...this.keywordsForAi,
                            {keyword: phrase, type: KeywordType.RELATED}
                        ];
                    }
                });
                this.persistBrief();
                this.jumpTo({...target, tag: 'p'});
                this.toastBrief('aiBrief.lsiInserted');
            },
            insertBriefFact(sentence: string): void {
                if (this.$store.getters.sharePreview || !sentence) {
                    return;
                }
                const article = this.$store.getters.article as Article;
                const existing = findPhraseInArticle(article, sentence);
                if (existing) {
                    this.jumpTo(existing);
                    this.rememberFact(sentence);
                    this.toastBrief('aiBrief.factExists');
                    return;
                }
                const target = firstBodyParagraph(article);
                this.ensureContentBlock(target.contentPart as 'h1' | 'h2', target.index);
                const current = (this.$store.getters.article as Article)[target.contentPart][target.index]?.p || '';
                this.$store.commit('setArticleContent', {
                    contentPart: target.contentPart,
                    index: target.index,
                    contentKey: 'p',
                    value: insertPhraseIntoHtml(current, sentence),
                });
                this.rememberFact(sentence);
                this.jumpTo({...target, tag: 'p'});
                this.toastBrief('aiBrief.factInserted');
            },
            workInLiveAnswer(platform: string): void {
                if (this.$store.getters.sharePreview || !platform) {
                    return;
                }
                const answer = (this.aiWritingBrief.answers || []).find((item) => item.platform === platform);
                const article = this.$store.getters.article as Article;
                const plan = planLiveAnswerWorkIn(answer, article);
                const locale = this.$i18n.locale === 'cs' ? 'cs' : 'en';
                const introSentences = [plan.lead, ...plan.introFacts].filter(Boolean);
                let inserted = 0;

                if (introSentences.length) {
                    const target = firstBodyParagraph(article);
                    this.ensureContentBlock(target.contentPart as 'h1' | 'h2', target.index);
                    const current = (this.$store.getters.article as Article)[target.contentPart][target.index]?.p || '';
                    const result = insertPhrasesIntoHtml(current, introSentences, 'fact', locale);
                    this.$store.commit('setArticleContent', {
                        contentPart: target.contentPart,
                        index: target.index,
                        contentKey: 'p',
                        value: result.html,
                    });
                    result.inserted.forEach((sentence) => {
                        this.rememberFact(sentence);
                        inserted += 1;
                    });
                }

                const byHeading = new Map<number, string[]>();
                for (const row of plan.headingFacts) {
                    const list = byHeading.get(row.index) || [];
                    list.push(row.sentence);
                    byHeading.set(row.index, list);
                }
                byHeading.forEach((sentences, index) => {
                    this.ensureContentBlock('h2', index);
                    const current = (this.$store.getters.article as Article).h2[index]?.p || '';
                    const result = insertPhrasesIntoHtml(current, sentences, 'fact', locale);
                    this.$store.commit('setArticleContent', {
                        contentPart: 'h2',
                        index,
                        contentKey: 'p',
                        value: result.html,
                    });
                    result.inserted.forEach((sentence) => {
                        this.rememberFact(sentence);
                        inserted += 1;
                    });
                });

                for (const question of plan.questions) {
                    const latest = this.$store.getters.article as Article;
                    if (findHeadingInArticle(latest, question)) {
                        continue;
                    }
                    const index = (latest.h2 || []).length;
                    this.$store.commit('insertArticleContentSubPart', {
                        contentPart: 'h2',
                        index,
                        keepContent: false,
                        aiLoading: false,
                    });
                    this.$store.commit('setArticleContent', {
                        contentPart: 'h2',
                        index,
                        contentKey: 'h',
                        value: question,
                    });
                    this.aiWritingBrief = rememberBriefValue(this.aiWritingBrief, 'introDirectives', question);
                    if (this.keywordsForAi.length < this.maxAiKeywords
                        && !this.keywordsForAi.some((item) => item.keyword.toLowerCase() === question.toLowerCase())) {
                        this.keywordsForAi = [
                            ...this.keywordsForAi,
                            {keyword: question, type: KeywordType.QUESTION}
                        ];
                    }
                    inserted += 1;
                }

                this.$set(this.aiWritingBrief, 'workedInPlatforms', markWorkedInPlatform(this.aiWritingBrief.workedInPlatforms, platform));
                this.persistBrief();

                this.toastBrief(inserted ? 'aiBrief.workInDone' : 'aiBrief.workInNothing');
            },
            applyHeadingToIntro(heading: string): void {
                if (this.$store.getters.sharePreview || !heading) {
                    return;
                }
                const article = this.$store.getters.article as Article;
                const existing = findPhraseInArticle(article, heading);
                if (existing && existing.contentPart === 'h1' && existing.tag === 'p') {
                    this.jumpTo(existing);
                    this.aiWritingBrief = rememberBriefValue(this.aiWritingBrief, 'introDirectives', heading);
                    this.persistBrief();
                    this.toastBrief('aiBrief.headingInIntro');
                    return;
                }
                const target = firstBodyParagraph(article);
                this.ensureContentBlock(target.contentPart as 'h1' | 'h2', target.index);
                const current = (this.$store.getters.article as Article)[target.contentPart][target.index]?.p || '';
                this.$store.commit('setArticleContent', {
                    contentPart: target.contentPart,
                    index: target.index,
                    contentKey: 'p',
                    value: insertPhraseIntoHtml(current, heading),
                });
                this.aiWritingBrief = rememberBriefValue(this.aiWritingBrief, 'introDirectives', heading);
                this.persistBrief();
                this.jumpTo({...target, tag: 'p'});
                this.toastBrief('aiBrief.headingInIntro');
            },
            addHeadingAsH2(heading: string): void {
                if (this.$store.getters.sharePreview || !heading) {
                    return;
                }
                const article = this.$store.getters.article as Article;
                const existing = findHeadingInArticle(article, heading);
                if (existing) {
                    this.jumpTo(existing);
                    this.toastBrief('aiBrief.headingExists');
                    return;
                }
                const index = (article.h2 || []).length;
                this.$store.commit('insertArticleContentSubPart', {
                    contentPart: 'h2',
                    index,
                    keepContent: false,
                    aiLoading: false,
                });
                this.$store.commit('setArticleContent', {
                    contentPart: 'h2',
                    index,
                    contentKey: 'h',
                    value: heading,
                });
                this.aiWritingBrief = rememberBriefValue(this.aiWritingBrief, 'introDirectives', heading);
                if (this.keywordsForAi.length < this.maxAiKeywords
                    && !this.keywordsForAi.some((item) => item.keyword.toLowerCase() === heading.toLowerCase())) {
                    this.keywordsForAi = [
                        ...this.keywordsForAi,
                        {keyword: heading, type: KeywordType.RELATED}
                    ];
                }
                this.persistBrief();
                this.jumpTo({contentPart: 'h2', index, tag: 'h'});
                this.toastBrief('aiBrief.headingAdded');
            },
            keepAnswerShape(): void {
                if (this.$store.getters.sharePreview) {
                    return;
                }
                this.$set(this.aiWritingBrief, 'keepAnswerShape', true);
                this.persistBrief();
                this.toastBrief('aiBrief.shapeKept');
            },
            toggleCitation(url: string): void {
                if (this.$store.getters.sharePreview || !url) {
                    return;
                }
                this.$set(this.aiWritingBrief, 'selectedCitationUrls', toggleCitationUrl(this.aiWritingBrief.selectedCitationUrls, url));
                this.persistBrief();
            }
        }
    });
</script>

<style scoped lang="sass">
#keywordTables
    scroll-margin-top: 88px

.keyword-write-cta-slot
    position: relative
    z-index: 2
    margin-bottom: 4px

.keyword-workspace-groups
    width: 100%
    margin: 12px auto 4px
    max-width: 100%

.kw-pair
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 10px

@media (max-width: 1260px)
    .kw-pair
        grid-template-columns: 1fr

.keyword-new-select
    width: 100%
    max-width: 800px
    min-width: 0
    box-sizing: border-box
    @media (max-width: 959px)
        width: calc(100vw - 36px)
        max-width: 800px

.kw-lucy-avatar
    display: inline-flex
    align-items: center
    justify-content: center
    width: 28px
    height: 28px
    border-radius: 50%
    background: linear-gradient(135deg, #7c4dff, #0c98f5)
    color: #fff
    font-weight: 800
    font-size: 14px
    margin-right: 10px
    flex-shrink: 0
</style>
